import { useState, useEffect, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Layout from '../components/Layout';
import { useToast } from '../contexts/ToastContext';
import { businessDataConnectionService, type BusinessDataConnection } from '../services/businessDataConnectionService';
import { businessDataService, type BusinessDataSensitive, type BusinessDataOperation, type BusinessDataIndexMetadata } from '../services/businessDataService';
import { Services, AuthMethods, OAUTH_SERVICES } from '../constants/services';
import { ConnectionCard } from '../components/connections/ConnectionCard';

// Cross-window contract with SalesforceOAuthCallbackPage (popup → wizard tab).
// The payload carries `service`, so one channel serves Salesforce and HubSpot.
const OAUTH_RESULT_TYPE = 'privacypal:salesforce-oauth-result';
const OAUTH_RESULT_STORAGE_KEY = 'privacypal:salesforce-oauth-result';

// ─── Service Definitions ──────────────────────────────────────────────────────

interface ServiceDef {
  id: string;
  name: string;
  auth: 'api_key' | 'connection_string';
  color: string;
  desc: string;
  perms: string;
  hint: string;
}

interface CategoryDef {
  key: string;
  title: string;
  icon: JSX.Element;
  services: ServiceDef[];
}


const DEFAULT_PORTS: Record<string, string> = { mssql: '1433', mongodb: '27017', mysql: '3306', supabase: '5432' };
const FILE_TYPES = ['PDF', 'DOCX', 'XLSX', 'PPTX', 'TXT', 'CSV', 'JSON', 'HTML', 'MD', 'RTF'];

// ─── SVG Logos ────────────────────────────────────────────────────────────────

function ServiceLogo({ id, size = 28 }: { id: string; size?: number }) {
  const s = size;
  const logos: Record<string, JSX.Element> = {
    google_drive: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M17.2 6L6 25.2l5.6 9.6L22.8 15.6z" fill="#0F9D58"/><path d="M30.8 6H17.2l11.2 19.2h13.2z" fill="#FBBC04"/><path d="M6 25.2l5.6 9.6h25.6l5.6-9.6z" fill="#4285F4"/></svg>,
    onedrive: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M19.5 36h18c4.1 0 7.5-3.4 7.5-7.5 0-3.5-2.4-6.4-5.6-7.2C39 16.1 34.6 12 29.2 12c-4.2 0-7.8 2.5-9.4 6.1C15.3 18 11 22.2 11 27.5 11 32.2 14.8 36 19.5 36z" fill="#0078D4"/></svg>,
    notion: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><rect x="8" y="6" width="32" height="36" rx="4" fill="#000"/><rect x="11" y="9" width="26" height="30" rx="2" fill="#fff"/><path d="M17 16h4.5l6 10V16H31v16h-4.2l-6.3-10.5V32H17V16z" fill="#000"/></svg>,
    box: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M24 10L8 20v12l16 10 16-10V20L24 10z" fill="#0061D5"/><path d="M24 10v22l16-10V20L24 10z" fill="#0061D5" opacity=".7"/><path d="M24 32L8 22v10l16 10V32z" fill="#003D8E"/></svg>,
    dropbox: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M14 8l10 6.4L14 21l-10-6.6L14 8zM34 8l10 6.4L34 21l-10-6.6L34 8zM14 27.2l10-6.4 10 6.4-10 6.6-10-6.6zM4 27.6L14 21l10 6.4-10 6.6L4 27.6zM34 21l10 6.6-10 6.4-10-6.4L34 21z" fill="#0061FE"/></svg>,
    netdocuments: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><rect x="4" y="4" width="40" height="40" rx="8" fill="#1B365D"/><text x="24" y="31" textAnchor="middle" fill="#fff" fontFamily="Inter,sans-serif" fontWeight="600" fontSize="16">nd</text></svg>,
    slack: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M19 6c-2.2 0-4 1.8-4 4s1.8 4 4 4h4V10c0-2.2-1.8-4-4-4z" fill="#E01E5A"/><path d="M19 18H10c-2.2 0-4 1.8-4 4s1.8 4 4 4h9c2.2 0 4-1.8 4-4s-1.8-4-4-4z" fill="#E01E5A"/><path d="M42 22c0-2.2-1.8-4-4-4s-4 1.8-4 4v4h4c2.2 0 4-1.8 4-4z" fill="#2EB67D"/><path d="M26 22v-9c0-2.2 1.8-4 4-4s4 1.8 4 4v9c0 2.2-1.8 4-4 4s-4-1.8-4-4z" fill="#2EB67D"/><path d="M30 42c2.2 0 4-1.8 4-4s-1.8-4-4-4h-4v4c0 2.2 1.8 4 4 4z" fill="#ECB22E"/><path d="M30 26h9c2.2 0 4 1.8 4 4s-1.8 4-4 4h-9c-2.2 0-4-1.8-4-4s1.8-4 4-4z" fill="#ECB22E"/><path d="M6 30c0 2.2 1.8 4 4 4s4-1.8 4-4v-4h-4c-2.2 0-4 1.8-4 4z" fill="#36C5F0"/><path d="M22 30v9c0 2.2-1.8 4-4 4s-4-1.8-4-4v-9c0-2.2 1.8-4 4-4s4 1.8 4 4z" fill="#36C5F0"/></svg>,
    teams: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><circle cx="32" cy="14" r="5" fill="#7B83EB"/><circle cx="20" cy="16" r="6" fill="#5059C9"/><path d="M33 22h8c1.7 0 3 1.3 3 3v8c0 3.3-2.7 6-6 6s-6-2.7-6-6v-10c0-.6.4-1 1-1z" fill="#7B83EB"/><path d="M28 22H12c-1.7 0-3 1.3-3 3v10c0 4.4 3.6 8 8 8h6c4.4 0 8-3.6 8-8V25c0-1.7-1.3-3-3-3z" fill="#5059C9"/></svg>,
    telegram: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="20" fill="#26A5E4"/><path d="M10.6 23.3l24.3-9.4c1.1-.4 2 .3 1.7 1.6l-4.1 19.5c-.3 1.3-1.1 1.6-2.2 1L24 31.4l-3.2 3.1c-.4.4-.7.6-1.3.6l.5-6.7 12-10.8c.5-.5-.1-.7-.8-.3l-14.9 9.4-6.4-2c-1.4-.4-1.4-1.4.3-2.1z" fill="#fff"/></svg>,
    mattermost: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="20" fill="#0058CC"/><path d="M33.5 14c-1.3-.8-5-2.5-9.5-2-6.5.7-11.5 6-11 12.5.5 5.5 5.3 10 11 10.3 0 0 2.5.2 5-.6l5 2.3-.8-5c2-3 2.3-5.5 2.3-7.5s-.5-4.5-2-10z" fill="#fff"/></svg>,
    salesforce: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M19.5 12c1.2-1.3 2.9-2 4.7-2 2.3 0 4.3 1.2 5.5 3 .8-.4 1.7-.6 2.6-.6 3.2 0 5.7 2.6 5.7 5.8 0 .5-.1 1-.2 1.5C39.3 20.5 41 22.8 41 25.5c0 3.7-3 6.7-6.7 6.7H14.8c-3.2 0-5.8-2.6-5.8-5.8 0-2.4 1.5-4.5 3.6-5.4-.1-.4-.1-.8-.1-1.2 0-3.5 2.8-6.3 6.3-6.3.6 0 1.2.1 1.7.3C20 13.2 19.7 12.6 19.5 12z" fill="#00A1E0"/></svg>,
    hubspot: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><circle cx="30" cy="28" r="7.5" stroke="#FF7A59" strokeWidth="4"/><path d="M30 20.5V12" stroke="#FF7A59" strokeWidth="4" strokeLinecap="round"/><circle cx="30" cy="10" r="3.5" fill="#FF7A59"/><path d="M24.2 23.4L13 15" stroke="#FF7A59" strokeWidth="4" strokeLinecap="round"/><circle cx="11" cy="13.5" r="3.5" fill="#FF7A59"/><path d="M24.8 33.6L18.5 39" stroke="#FF7A59" strokeWidth="4" strokeLinecap="round"/><circle cx="17" cy="40.5" r="3" fill="#FF7A59"/></svg>,
    mssql: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="12" rx="14" ry="6" fill="#CC2927"/><path d="M10 12v24c0 3.3 6.3 6 14 6s14-2.7 14-6V12c0 3.3-6.3 6-14 6S10 15.3 10 12z" fill="#CC2927"/><ellipse cx="24" cy="12" rx="14" ry="6" fill="#F04E3C" opacity=".5"/></svg>,
    mongodb: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M25.5 5.5c-.8 2-1.3 4-1.5 6-.3 3-.2 6 .5 9 .6 2.5 1.8 4.8 3.5 6.8 2.2 2.5 3.5 5.3 3.5 8.7v2h-3v-1c0-1.3-.3-2.6-.7-3.8-.5-1.3-1.3-2.5-2.3-3.5-2-2.2-3.3-4.8-4-7.7-.5-2.5-.5-5-.3-7.5.3-3 1-5.7 2.5-8.2.5-.7.9-1.2 1.3-1.6l.5.8z" fill="#00684A"/><path d="M24 42h1v4h-2v-4h1z" fill="#B8C4BF"/></svg>,
    mysql: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="20" fill="#00758F"/><text x="24" y="30" textAnchor="middle" fill="#fff" fontFamily="Inter,sans-serif" fontWeight="600" fontSize="14">My</text></svg>,
    supabase: <svg width={s} height={s} viewBox="0 0 48 48" fill="none"><path d="M26.3 43.7c-1 1.3-3.2.5-3.2-1.2V27h17.4c2.3 0 3.6 2.7 2.1 4.4L26.3 43.7z" fill="#3ECF8E"/><path d="M21.7 4.3c1-1.3 3.2-.5 3.2 1.2V21H7.5c-2.3 0-3.6-2.7-2.1-4.4L21.7 4.3z" fill="#3ECF8E"/></svg>,
  };
  return logos[id] || <div className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-700" />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ConnectionsPage() {
  const { t } = useLingui();
  const { showToast } = useToast();
  const [connections, setConnections] = useState<BusinessDataConnection[]>([]);
  const [loading, setLoading] = useState(true);

  // Business Data catalog state
  const [businessDataCatalog, setBusinessDataCatalog] = useState<BusinessDataSensitive[]>([]);
  const [businessDataMetadata, setBusinessDataMetadata] = useState<BusinessDataIndexMetadata | null>(null);
  const [businessDataScanJobs, setBusinessDataScanJobs] = useState<BusinessDataOperation[]>([]);
  const [businessDataLoading, setBusinessDataLoading] = useState(false);
  const [scanningIds, setScanningIds] = useState<Set<string>>(new Set());
  const [businessDataExpanded, setBusinessDataExpanded] = useState(false);

  // Scan-job polling: POST /api/businessData/scan returns 200 on *start*, not completion,
  // so we poll the catalog until each connection's job reaches a terminal state.
  const mountedRef = useRef(true);
  const pollTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Wizard state
  const [wizOpen, setWizOpen] = useState(false);
  const [wizStep, setWizStep] = useState(1);
  const [wizService, setWizService] = useState<ServiceDef | null>(null);
  const [wizCategory, setWizCategory] = useState('');
  const [wizSubmitting, setWizSubmitting] = useState(false);
  const [wizError, setWizError] = useState('');
  const [wizSuccess, setWizSuccess] = useState(false);
  const [wizConnection, setWizConnection] = useState<BusinessDataConnection | null>(null);

  // Wizard form fields
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('');
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbPass, setDbPass] = useState('');
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([...FILE_TYPES]);
  const [passthroughAuth, setPassthroughAuth] = useState(false);
  const [scanPrivate, setScanPrivate] = useState(false);
  const [filterBots, setFilterBots] = useState(true);
  const [excludedTables, setExcludedTables] = useState('');
  const [fullTextSearch, setFullTextSearch] = useState(false);
  // CRM deep-scan exclusions (Salesforce Object.Field, HubSpot objectType.property)
  const [excludedCrmFields, setExcludedCrmFields] = useState('');
  // Salesforce "bring-your-own Connected App" fields (per-tenant OAuth)
  const [sfLoginUrl, setSfLoginUrl] = useState('');
  const [sfClientId, setSfClientId] = useState('');
  const [sfClientSecret, setSfClientSecret] = useState('');
  const [showSfSecret, setShowSfSecret] = useState(false);
  // HubSpot: the customer's own HubSpot app (OAuth), or a static access token
  // from an app created in their own account.
  const [hsAuthMode, setHsAuthMode] = useState<'oauth' | 'token'>('oauth');
  const [hsClientId, setHsClientId] = useState('');
  const [hsClientSecret, setHsClientSecret] = useState('');
  const [showHsSecret, setShowHsSecret] = useState(false);
  const [hsToken, setHsToken] = useState('');
  const [showHsToken, setShowHsToken] = useState(false);
  // Callback URL + scopes to register in the customer's own app (server-derived)
  const [oauthRedirectUri, setOauthRedirectUri] = useState('');
  const [oauthScopes, setOauthScopes] = useState<string[]>([]);
  const [oauthOptionalScopes, setOauthOptionalScopes] = useState<string[]>([]);

  // Guards against double-handling when both postMessage and the storage
  // fallback deliver the same OAuth result. Reset before each attempt.
  const oauthHandledRef = useRef(false);
  // The wizard's service, readable from the mount-time OAuth result listener.
  const wizServiceRef = useRef<ServiceDef | null>(null);
  // Tracks the OAuth popup and the interval that watches for the user closing it
  // (cancellation) before a result arrives.
  const oauthPopupRef = useRef<Window | null>(null);
  const oauthPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearOAuthPoll = () => {
    if (oauthPollRef.current !== null) {
      clearInterval(oauthPollRef.current);
      oauthPollRef.current = null;
    }
    oauthPopupRef.current = null;
  };

  useEffect(() => { loadConnections(); loadBusinessDataCatalog(); }, []);

  // Listen for the OAuth result (Salesforce or HubSpot) reported back by the
  // popup callback page. Registered once on mount, so it must not read React
  // state captured at mount time — it reads the wizard service from a ref and
  // the success branch refetches connections directly instead.
  useEffect(() => {
    const handleOAuthResult = async (payload: { success?: boolean; service?: string; error?: string }) => {
      const svc = wizServiceRef.current;
      if (!svc || payload.service !== svc.id) return;
      if (oauthHandledRef.current) return;
      oauthHandledRef.current = true;
      clearOAuthPoll();

      setWizSubmitting(false);

      if (payload.success) {
        let conns: BusinessDataConnection[] = [];
        try {
          conns = await businessDataConnectionService.getConnections();
          setConnections(conns);
        } catch {
          // Non-fatal: the connection exists server-side; the user can retry the
          // refresh by reopening the page. Still advance the wizard.
        }
        const connected =
          conns.find(c => c.serviceType === svc.id && c.authMethod === AuthMethods.OAuth2) ||
          conns.find(c => c.serviceType === svc.id) ||
          null;
        setWizConnection(connected);
        setWizError('');
        setWizSuccess(false);
        setWizStep(3);
        const serviceName = svc.name;
        showToast(t`${serviceName} connected successfully`, 'success');
      } else {
        const serviceName = svc.name;
        const message = payload.error || t`${serviceName} authentication failed`;
        setWizStep(2);
        setWizError(message);
        showToast(message, 'error');
      }
    };

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || data.type !== OAUTH_RESULT_TYPE || !OAUTH_SERVICES.includes(data.service)) return;
      handleOAuthResult(data);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== OAUTH_RESULT_STORAGE_KEY || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue);
        if (!data || data.type !== OAUTH_RESULT_TYPE || !OAUTH_SERVICES.includes(data.service)) return;
        handleOAuthResult(data);
      } catch {
        // Ignore malformed payloads.
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Stop all polling and block stray setState once the page unmounts.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pollTimersRef.current.forEach(clearTimeout);
      pollTimersRef.current.clear();
      clearOAuthPoll();
    };
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await businessDataConnectionService.getConnections();
      setConnections(data);
    } catch (error: any) {
      if (error.response?.status !== 403) {
        showToast(error.response?.data?.error || t`Failed to load connections`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessDataCatalog = async () => {
    try {
      setBusinessDataLoading(true);
      const data = await businessDataService.getCatalog();
      setBusinessDataCatalog(data.catalog || []);
      setBusinessDataMetadata(data.metadata || null);
      setBusinessDataScanJobs(data.scanJobs || []);
    } catch {
      // Business Data catalog may not exist yet — fail silently
    } finally {
      setBusinessDataLoading(false);
    }
  };

  const clearScanning = (connId: string) =>
    setScanningIds(s => { const n = new Set(s); n.delete(connId); return n; });

  // Poll the catalog until this connection's scan job is terminal (completed/error)
  // or a hard timeout is reached. setTimeout recursion (not setInterval) avoids
  // overlapping in-flight requests; timers are tracked so unmount can cancel them.
  const POLL_INTERVAL_MS = 3000;
  const POLL_TIMEOUT_MS = 2 * 60 * 1000;

  const pollScanJob = (connId: string) => {
    const startedAt = Date.now();

    const scheduleNext = () => {
      const timer = setTimeout(() => {
        pollTimersRef.current.delete(timer);
        tick();
      }, POLL_INTERVAL_MS);
      pollTimersRef.current.add(timer);
    };

    const tick = async () => {
      if (!mountedRef.current) return;
      let jobs: BusinessDataOperation[] = [];
      try {
        const data = await businessDataService.getCatalog();
        if (!mountedRef.current) return;
        setBusinessDataCatalog(data.catalog || []);
        setBusinessDataMetadata(data.metadata || null);
        setBusinessDataScanJobs(data.scanJobs || []);
        jobs = data.scanJobs || [];
      } catch {
        // Transient fetch error — keep polling until the timeout.
      }
      const job = jobs.find(j => j.connectionId === connId);
      if (job && (job.status === 'completed' || job.status === 'error')) {
        clearScanning(connId);
        if (job.status === 'error') {
          const detail = job.error || t`unknown error`;
          showToast(t`Scan failed: ${detail}`, 'error');
        }
        return;
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        clearScanning(connId);
        showToast(t`Scan is still running — refresh in a moment to see results.`, 'success');
        return;
      }
      scheduleNext();
    };

    scheduleNext();
  };

  const handleScanConnection = async (conn: BusinessDataConnection) => {
    setScanningIds(s => new Set(s).add(conn.id));
    try {
      await businessDataService.scanConnection(conn.id);
      showToast(t`Scanning ${conn.serviceName} for sensitive data…`, 'success');
      // 200 means "scan started" — poll until the job reaches a terminal state.
      pollScanJob(conn.id);
    } catch (error: any) {
      // Only clear on start failure; the success path clears when polling finishes.
      showToast(error.response?.data?.error || t`Failed to start scan`, 'error');
      clearScanning(conn.id);
    }
  };

  const handleSyncAll = async () => {
    setBusinessDataLoading(true);
    try {
      await businessDataService.syncAll();
      showToast(t`Full Business Data sync started — this may take a few minutes`, 'success');
      setTimeout(() => loadBusinessDataCatalog(), 8000);
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to start sync`, 'error');
    } finally {
      setBusinessDataLoading(false);
    }
  };

  const openWizard = (svc: ServiceDef, catKey: string) => {
    oauthHandledRef.current = false;
    wizServiceRef.current = svc;
    setWizService(svc);
    setWizCategory(catKey);
    setWizStep(1);
    setWizSubmitting(false);
    setWizError('');
    setWizSuccess(false);
    setWizConnection(null);
    setApiKey('');
    setShowKey(false);
    setDbHost('');
    setDbPort(DEFAULT_PORTS[svc.id] || '');
    setDbName('');
    setDbUser('');
    setDbPass('');
    setSelectedFileTypes([...FILE_TYPES]);
    setPassthroughAuth(false);
    setScanPrivate(false);
    setFilterBots(true);
    setExcludedTables('');
    setFullTextSearch(false);
    setExcludedCrmFields('');
    setSfLoginUrl('');
    setSfClientId('');
    setSfClientSecret('');
    setShowSfSecret(false);
    setHsAuthMode('oauth');
    setHsClientId('');
    setHsClientSecret('');
    setShowHsSecret(false);
    setHsToken('');
    setShowHsToken(false);
    setOauthRedirectUri('');
    setOauthScopes([]);
    setOauthOptionalScopes([]);
    // Prefetch the exact callback URL + scopes the customer must register in
    // their own app (server-derived so it matches AUTH_CALLBACK_BASE_URL).
    if (OAUTH_SERVICES.includes(svc.id)) {
      businessDataConnectionService.getOAuthConfig(svc.id)
        .then(cfg => { setOauthRedirectUri(cfg.redirectUri); setOauthScopes(cfg.scopes); setOauthOptionalScopes(cfg.optionalScopes); })
        .catch(() => {});
    }
    setWizOpen(true);
  };

  const closeWizard = () => {
    setWizOpen(false);
    // Always refresh on close (not only on success) so a connection that was
    // created but failed verification surfaces immediately as an auth-failed card.
    loadConnections();
  };

  const handleDisconnect = async (conn: BusinessDataConnection) => {
    try {
      await businessDataConnectionService.deleteConnection(conn.id);
      showToast(t`${conn.serviceName} disconnected`, 'success');
      loadConnections();
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to disconnect`, 'error');
    }
  };

  // Reconnect an auth-failed connection: drop the broken record, then reopen the
  // wizard so the admin can enter fresh credentials.
  const handleReconnect = async (conn: BusinessDataConnection, svc: ServiceDef, catKey: string) => {
    try {
      await businessDataConnectionService.deleteConnection(conn.id);
      await loadConnections();
      openWizard(svc, catKey);
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to start reconnect`, 'error');
    }
  };

  const submitConnection = async () => {
    if (!wizService) return;

    const crmExcludedFields = () => excludedCrmFields.split(/[\n,]/).map(s => s.trim()).filter(Boolean);

    // OAuth services start authorization from step 2 ("Connect with Salesforce"
    // / "Connect with HubSpot"); here at step 3 we only persist the deep-scan
    // configuration. No credentials are sent — the verifier-controlled status
    // and tokens are preserved server-side.
    const usesOAuth = wizService.id === Services.Salesforce || (wizService.id === Services.HubSpot && hsAuthMode === 'oauth');
    if (usesOAuth) {
      if (!wizConnection?.id) {
        const serviceName = wizService.name;
        setWizError(t`Complete ${serviceName} authentication before saving configuration.`);
        return;
      }
      const config = { excludedFields: crmExcludedFields() };
      setWizSubmitting(true);
      setWizError('');
      try {
        const updated = await businessDataConnectionService.updateConnection(wizConnection.id, { config });
        setWizConnection(updated);
        setWizStep(4);
        setWizSuccess(true);
      } catch (error: any) {
        setWizError(error.response?.data?.error || error.message || t`Failed to save configuration`);
      } finally {
        setWizSubmitting(false);
      }
      return;
    }

    setWizStep(4);
    setWizSubmitting(true);
    setWizError('');
    setWizSuccess(false);
    setWizConnection(null);

    let credentials = '';
    if (wizService.auth === 'connection_string') {
      credentials = JSON.stringify({ host: dbHost, port: dbPort, database: dbName, username: dbUser, password: dbPass });
    } else if (wizService.id === Services.HubSpot) {
      // Static access token path; verified server-side with an authenticated read.
      credentials = JSON.stringify({ accessToken: hsToken.trim() });
    } else {
      credentials = apiKey;
    }

    let config: Record<string, any> = {};
    if (wizCategory === 'document_repositories') {
      config = { fileTypes: selectedFileTypes, passthroughAuth };
    } else if (wizCategory === 'collaboration_tools') {
      config = { scanPrivateChannels: scanPrivate, filterBots };
    } else if (wizCategory === 'databases') {
      config = { excludedTables: excludedTables.split(',').map(s => s.trim()).filter(Boolean), fullTextSearch };
    } else if (wizCategory === 'crm') {
      // CRM deep-scan: optional list of "Object.FieldApiName" (Salesforce) or
      // "objectType.property" (HubSpot) entries to skip.
      config = { excludedFields: crmExcludedFields() };
    }

    try {
      const connection = await businessDataConnectionService.createConnection({
        serviceType: wizService.id,
        serviceName: wizService.name,
        category: wizCategory,
        authMethod: wizService.auth,
        credentials,
        config,
      });

      setWizConnection(connection);
      if (connection.status === 'error') {
        setWizError(connection.lastError || t`BusinessDataConnection verification failed`);
        return;
      }
      if (OAUTH_SERVICES.includes(wizService.id) && connection.status !== 'active') {
        const serviceName = wizService.name;
        setWizError(t`${serviceName} connection could not be verified`);
        return;
      }
      setWizSuccess(true);
    } catch (error: any) {
      setWizError(error.response?.data?.error || error.message || t`Failed to create connection`);
    } finally {
      setWizSubmitting(false);
    }
  };

  const startOAuth = async () => {
    if (!wizService) return;
    const serviceId = wizService.id;
    const serviceName = wizService.name;
    oauthHandledRef.current = false;
    clearOAuthPoll();
    setWizSubmitting(true);
    setWizError('');
    try {
      const appCreds = serviceId === Services.HubSpot
        ? { clientId: hsClientId.trim(), clientSecret: hsClientSecret.trim() }
        : { loginUrl: sfLoginUrl.trim(), clientId: sfClientId.trim(), clientSecret: sfClientSecret };
      const { authorizeUrl, connectionId } = await businessDataConnectionService.startOAuth(serviceId, appCreds);
      if (!authorizeUrl) throw new Error(t`${serviceName} OAuth start did not return an authorization URL`);

      // Open OAuth in a popup so the wizard stays mounted in this tab; the popup
      // posts its result back via SalesforceOAuthCallbackPage. Never navigate here.
      let popup = window.open(authorizeUrl, `privacypal-${serviceId}-oauth`, 'width=600,height=720');
      if (!popup) popup = window.open(authorizeUrl, '_blank');
      if (!popup) {
        setWizError(t`Popup blocked. Please allow popups for this site and try again.`);
        setWizSubmitting(false);
        return;
      }

      // Watch for the user closing the popup before a result arrives — treat that
      // as a cancellation. The result handler clears this interval if it wins.
      oauthPopupRef.current = popup;
      oauthPollRef.current = setInterval(() => {
        if (!oauthPopupRef.current || !oauthPopupRef.current.closed) return;
        clearOAuthPoll();
        if (oauthHandledRef.current) return;
        oauthHandledRef.current = true;
        setWizSubmitting(false);
        setWizStep(2);
        setWizError(t`${serviceName} authentication was cancelled.`);
        // Clean up the orphaned pending connection staged by startOAuth.
        if (connectionId) businessDataConnectionService.deleteConnection(connectionId).catch(() => {});
      }, 500);
      // Leave wizSubmitting true while the popup runs — the message/storage
      // handler clears it once the OAuth result arrives.
    } catch (error: any) {
      setWizError(error.response?.data?.error || error.message || t`Failed to start ${serviceName} OAuth`);
      setWizSubmitting(false);
    }
  };

  // ─── Step Indicator ───────────────────────────────────────────────────

  const stepLabels = [t`Welcome`, t`Authenticate`, t`Configure`, t`Complete`];
  const StepIndicator = () => (
    <div className="flex items-center gap-1.5 px-6 pb-5">
      {stepLabels.map((label, i) => {
        const num = i + 1;
        const done = num < wizStep;
        const active = num === wizStep;
        return (
          <div key={num} className="flex items-center gap-1.5 flex-1 last:flex-none">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors ${
              done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'text-slate-400'
            }`} style={!done && !active ? { backgroundColor: 'var(--color-surface-secondary)' } : {}}>
              {done ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              ) : num}
            </div>
            <span className={`text-[10px] font-semibold uppercase tracking-wider hidden sm:inline ${done ? 'text-green-500' : active ? 'text-blue-600 dark:text-blue-400' : ''}`} style={!done && !active ? { color: 'var(--color-text-muted)' } : {}}>
              {label}
            </span>
            {i < stepLabels.length - 1 && <div className="flex-1 h-px mx-1" style={{ backgroundColor: 'var(--color-border)' }} />}
          </div>
        );
      })}
    </div>
  );

  // ─── Toggle Component ─────────────────────────────────────────────────

  const Toggle = ({ on, onToggle, label, desc }: { on: boolean; onToggle: () => void; label: string; desc: string }) => (
    <div className="flex items-center justify-between rounded-xl border p-4" style={{ borderColor: 'var(--color-border)' }}>
      <div className="pr-4">
        <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{desc}</p>
      </div>
      <button type="button" onClick={onToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${on ? 'bg-blue-600' : ''}`} style={!on ? { backgroundColor: 'var(--color-surface-secondary)' } : {}}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );

  // ─── Service Definitions ──────────────────────────────────────────────

  // Defined inside the component so the customer-facing copy (title, desc,
  // perms, hint) is translated. Service names stay untranslated: they are
  // product names and are sent to the API as serviceName.
  const CATEGORIES: CategoryDef[] = [
    {
      key: 'document_repositories',
      title: t`Document Repositories`,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>,
      services: [
        { id: 'google_drive', name: 'Google Drive', auth: 'api_key', color: '#4285F4', desc: t`Connect to Google Drive to scan documents, spreadsheets, and presentations stored in your organization's workspace.`, perms: t`Read-only access to files, folder listing, and metadata. Requires a Google Cloud service account key with Drive API enabled.`, hint: t`Paste your Google Cloud service account JSON key or API key` },
        { id: 'onedrive', name: 'Microsoft OneDrive', auth: 'api_key', color: '#0078D4', desc: t`Integrate with OneDrive for Business to monitor files shared across your Microsoft 365 tenant.`, perms: t`Files.Read.All application permission via Microsoft Graph API. Requires an Azure AD app registration.`, hint: t`Enter your Azure AD application client secret` },
        { id: 'notion', name: 'Notion', auth: 'api_key', color: '#000000', desc: t`Connect to Notion workspaces to scan pages, databases, and embedded content for sensitive data.`, perms: t`Read content capability on the internal integration. Create an integration at notion.so/my-integrations.`, hint: t`Paste your Notion internal integration token (secret_...)` },
        { id: 'box', name: 'Box', auth: 'api_key', color: '#0061D5', desc: t`Scan files stored in Box cloud storage across all enterprise folders and collaborations.`, perms: t`Read access to all files and folders. Requires a Box custom app with Server Authentication (JWT).`, hint: t`Paste your Box application JSON config or developer token` },
        { id: 'dropbox', name: 'Dropbox', auth: 'api_key', color: '#0061FE', desc: t`Connect to Dropbox Business to monitor shared files, Paper documents, and team folders.`, perms: t`team_data.member access via Dropbox Business API. Requires a scoped app on the Dropbox App Console.`, hint: t`Paste your Dropbox app access token` },
        { id: 'netdocuments', name: 'NetDocuments', auth: 'api_key', color: '#1B365D', desc: t`Integrate with NetDocuments to scan legal documents, matter files, and workspace content.`, perms: t`Read access to cabinets and workspaces. Requires API client credentials from the NetDocuments admin console.`, hint: t`Enter your NetDocuments client ID and secret separated by a colon` },
      ],
    },
    {
      key: 'collaboration_tools',
      title: t`Collaboration Tools`,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" /></svg>,
      services: [
        { id: 'slack', name: 'Slack', auth: 'api_key', color: '#E01E5A', desc: t`Monitor Slack messages across channels and direct messages for sensitive data and PII exposure.`, perms: t`channels:history, groups:history, im:history, users:read scopes. Create a Slack app with Bot Token permissions.`, hint: t`Paste your Slack Bot User OAuth Token (xoxb-...)` },
        { id: 'teams', name: 'Microsoft Teams', auth: 'api_key', color: '#6264A7', desc: t`Scan Microsoft Teams channels, chats, and shared files for privacy-sensitive content.`, perms: t`ChannelMessage.Read.All, Chat.Read.All via Microsoft Graph. Requires Azure AD app with application permissions.`, hint: t`Enter your Azure AD application client secret` },
        { id: 'telegram', name: 'Telegram', auth: 'api_key', color: '#26A5E4', desc: t`Connect to Telegram groups and channels used by your organization to detect sensitive data sharing.`, perms: t`Bot API access with read permissions on target groups. Create a bot via @BotFather and add it to your groups.`, hint: t`Paste your Telegram Bot API token from @BotFather` },
        { id: 'mattermost', name: 'Mattermost', auth: 'api_key', color: '#0058CC', desc: t`Integrate with your self-hosted or cloud Mattermost instance to scan channels for privacy risks.`, perms: t`System Admin personal access token or bot account with read access to channels and posts.`, hint: t`Enter your Mattermost personal access token` },
      ],
    },
    {
      key: 'crm',
      title: t`CRM & Business Systems`,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>,
      services: [
        {
          id: 'salesforce',
          name: 'Salesforce',
          auth: 'api_key',
          color: '#00A1E0',
          desc: t`Scan Salesforce Contacts, Leads, Accounts, Cases, and Opportunities to catalog sensitive customer data and ensure AI prompts never expose CRM records.`,
          perms: t`Your own Salesforce Connected App (OAuth 2.0 Authorization Code + Refresh Token). Enable OAuth, register the callback URL shown in the wizard, and grant the api and refresh_token scopes with read access to Contact, Lead, Account, Case, Opportunity.`,
          hint: '',
        },
        {
          id: 'hubspot',
          name: 'HubSpot',
          auth: 'api_key',
          color: '#FF7A59',
          desc: t`Scan HubSpot contacts, companies, deals and custom objects to catalog customer names, account names and deal details, so AI prompts never expose CRM records.`,
          perms: t`Your own HubSpot app (OAuth 2.0, recommended) with the callback URL shown in the wizard and read access to contacts, companies and deals, or an access token from a service key or legacy private app in your HubSpot account with the same read scopes.`,
          hint: '',
        },
      ],
    },
    {
      key: 'databases',
      title: t`Databases`,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" /></svg>,
      services: [
        { id: 'mssql', name: 'MS SQL Server', auth: 'connection_string', color: '#CC2927', desc: t`Connect to Microsoft SQL Server instances to catalog and scan database content for PII.`, perms: t`Read-only access (db_datareader role) to target databases. Service account with SELECT permission on relevant schemas.`, hint: '' },
        { id: 'mongodb', name: 'MongoDB', auth: 'connection_string', color: '#00684A', desc: t`Scan MongoDB collections for personally identifiable information and sensitive data patterns.`, perms: t`Read-only user with find permission on target databases/collections. Connection via standard MongoDB URI.`, hint: '' },
        { id: 'mysql', name: 'MySQL', auth: 'connection_string', color: '#00758F', desc: t`Connect to MySQL or MariaDB instances to identify and catalog sensitive data across tables.`, perms: t`Read-only user with SELECT privilege on target databases. CREATE USER with limited grants recommended.`, hint: '' },
        { id: 'supabase', name: 'Supabase', auth: 'connection_string', color: '#3ECF8E', desc: t`Integrate with your Supabase project to scan PostgreSQL tables and storage buckets.`, perms: t`Service role key or direct database connection with read-only access. Find credentials in Supabase dashboard > Settings > API.`, hint: '' },
      ],
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  // Prefer an active connection; otherwise surface an errored (auth-failed) one
  // so it doesn't silently disappear into a "Connect" card.
  const getConnection = (serviceId: string) =>
    connections.find(c => c.serviceType === serviceId && c.status === 'active')
    ?? connections.find(c => c.serviceType === serviceId && c.status === 'error');
  const activeCount = connections.filter(c => c.status === 'active').length;
  const catalogCount = businessDataMetadata?.entryCount ?? 0;
  const lastSyncDate = businessDataMetadata?.lastSyncAt
    ? new Date(businessDataMetadata.lastSyncAt).toLocaleDateString()
    : '';

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}><Trans>Connections</Trans></h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              <Trans>Connect third-party services to enable PrivacyPal to scan and protect sensitive data across your organization.</Trans>
            </p>
          </div>
          {activeCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-800/40">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-semibold text-green-600 dark:text-green-400"><Trans>{activeCount} Active</Trans></span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : (
          CATEGORIES.map(cat => (
            <section key={cat.key}>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="text-blue-600 dark:text-blue-400">{cat.icon}</span>
                <h2 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>{cat.title}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cat.services.map(svc => {
                  const conn = getConnection(svc.id);
                  const scanJob = conn ? businessDataScanJobs.find(j => j.connectionId === conn.id) : undefined;
                  const isScanning = conn ? scanningIds.has(conn.id) || scanJob?.status === 'running' : false;
                  const isLegacySalesforce = svc.id === Services.Salesforce && conn && conn.authMethod !== AuthMethods.OAuth2;
                  return (
                    <ConnectionCard
                      key={svc.id}
                      name={svc.name}
                      logo={<ServiceLogo id={svc.id} size={24} />}
                      conn={conn}
                      scanJob={scanJob}
                      isScanning={isScanning}
                      isLegacy={!!isLegacySalesforce}
                      onConnect={() => openWizard(svc, cat.key)}
                      onScan={handleScanConnection}
                      onReconnect={(c) => handleReconnect(c, svc, cat.key)}
                      onUpgrade={() => openWizard(svc, cat.key)}
                      onDisconnect={handleDisconnect}
                    />
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      {/* ─── Business Data Catalog Section ─────────────────────────────────────────── */}
      {activeCount > 0 && (
        <section className="mt-8">
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                </div>
                <div>
                  <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>Business Data Catalog</Trans></h2>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {businessDataMetadata ? (
                      businessDataMetadata.lastSyncAt
                        ? <Trans>{catalogCount} items indexed · Last sync {lastSyncDate}</Trans>
                        : <Trans>{catalogCount} items indexed</Trans>
                    ) : (
                      <Trans>Scan connections to build your catalog</Trans>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSyncAll}
                  disabled={businessDataLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-500/20 disabled:opacity-50"
                >
                  {businessDataLoading ? (
                    <><span className="w-3 h-3 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" /><Trans>Syncing…</Trans></>
                  ) : (
                    <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg><Trans>Sync All</Trans></>
                  )}
                </button>
                {businessDataCatalog.length > 0 && (
                  <button onClick={() => setBusinessDataExpanded(v => !v)} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ color: 'var(--color-text-muted)' }}>
                    {businessDataExpanded ? <Trans>Collapse</Trans> : <Trans>Expand</Trans>}
                    <svg className={`w-3.5 h-3.5 transition-transform ${businessDataExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                )}
              </div>
            </div>

            {/* Category summary */}
            {businessDataCatalog.length > 0 && (
              <div className="px-5 py-3 flex flex-wrap gap-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
                {(['PII', 'PCI', 'PHI', 'TECHNICAL', 'OTHER'] as const).map(cat => {
                  const count = businessDataCatalog.filter(e => e.category === cat).length;
                  if (!count) return null;
                  const colors: Record<string, string> = { PII: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400', PCI: 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400', PHI: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400', TECHNICAL: 'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-400', OTHER: 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-500' };
                  return (
                    <span key={cat} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${colors[cat]}`}>
                      {cat} <span className="opacity-70">· {count}</span>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Catalog entries (expandable) */}
            {businessDataExpanded && businessDataCatalog.length > 0 && (
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {businessDataCatalog.slice(0, 100).map(entry => (
                  <div key={entry.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-muted)' }}>{entry.entityType}</span>
                      {/* Values are stored as one-way hashes — show field provenance, never plaintext. */}
                      <span className="text-sm truncate font-medium" style={{ color: 'var(--color-text)' }}>
                        {entry.sourceObject
                          ? `${entry.sourceObject}${entry.fieldApiName ? ` · ${entry.fieldApiName}` : ''}`
                          : entry.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{entry.sourceType}</span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{Math.round((entry.confidence || 0) * 100)}%</span>
                    </div>
                  </div>
                ))}
                {businessDataCatalog.length > 100 && (
                  <div className="px-5 py-3 text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>Showing 100 of {businessDataCatalog.length} entries</Trans>
                  </div>
                )}
              </div>
            )}

            {businessDataCatalog.length === 0 && !businessDataLoading && (
              <div className="px-5 py-8 text-center">
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>No sensitive data indexed yet. Click "Scan" on any active connection or "Sync All" to begin.</Trans></p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Wizard Modal ──────────────────────────────────────────────────── */}
      {wizOpen && wizService && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={closeWizard} />
          <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
            <div className="rounded-2xl max-w-lg w-full shadow-2xl border overflow-hidden animate-slide-in" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center border shadow-sm" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                    <ServiceLogo id={wizService.id} size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>{wizService.name}</h3>
                    <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--color-text-muted)' }}>
                      {wizCategory.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>
                <button onClick={closeWizard} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100 dark:hover:bg-white/10" style={{ color: 'var(--color-text-muted)' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <StepIndicator />

              {/* Steps */}
              <div className="px-6 pb-6 min-h-[320px] flex flex-col">

                {/* Step 1: Welcome */}
                {wizStep === 1 && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1">
                      <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--color-text-secondary)' }}>{wizService.desc}</p>
                      <div className="rounded-xl border p-4 mb-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Secure Connection</Trans></p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}><Trans>All credentials are encrypted at rest and transmitted over TLS. Only domain administrators can manage connections.</Trans></p>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-xl border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Required Permissions</Trans></p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{wizService.perms}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => setWizStep(2)} className="mt-5 w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm">
                      <Trans>Get Started</Trans>
                    </button>
                  </div>
                )}

                {/* Step 2: Authentication */}
                {wizStep === 2 && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Authenticate your connection</Trans></p>
                      </div>

                      {wizService.id === Services.Salesforce ? (
                        <>
                          <div className="rounded-xl border p-4 space-y-2.5" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Use your own Salesforce Connected App</Trans></p>
                            <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                              <Trans>In your Salesforce org, create a Connected App with OAuth enabled, register the exact callback URL below, and grant the scopes shown. Then paste your My Domain URL and the app's Consumer Key/Secret. You approve access in a popup — tokens are exchanged server-side and stored encrypted.</Trans>
                            </p>
                            <div>
                              <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Callback URL to register</Trans></label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 rounded-lg text-xs break-all" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}>{oauthRedirectUri || '…'}</code>
                                <button type="button" onClick={() => { if (oauthRedirectUri) { navigator.clipboard?.writeText(oauthRedirectUri); showToast(t`Callback URL copied`, 'success'); } }} className="px-3 py-2 rounded-lg text-xs font-medium flex-shrink-0" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}><Trans>Copy</Trans></button>
                              </div>
                            </div>
                            <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}><Trans>Required OAuth scopes: <span className="font-mono">{(oauthScopes.length ? oauthScopes : ['api', 'refresh_token']).join(', ')}</span></Trans></p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>My Domain login URL</Trans></label>
                            <input type="text" value={sfLoginUrl} onChange={e => { setSfLoginUrl(e.target.value); setWizError(''); }} placeholder="https://yourcompany.my.salesforce.com" className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Consumer Key</Trans></label>
                            <input type="text" value={sfClientId} onChange={e => { setSfClientId(e.target.value); setWizError(''); }} placeholder={t`Connected App Consumer Key`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Consumer Secret</Trans></label>
                            <div className="relative">
                              <input
                                type={showSfSecret ? 'text' : 'password'}
                                value={sfClientSecret}
                                onChange={e => { setSfClientSecret(e.target.value); setWizError(''); }}
                                placeholder={t`Connected App Consumer Secret`}
                                className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-mono"
                                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                              />
                              <button type="button" onClick={() => setShowSfSecret(!showSfSecret)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {showSfSecret
                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                                  }
                                </svg>
                              </button>
                            </div>
                          </div>
                          {wizError && <p className="text-xs text-red-500">{wizError}</p>}
                        </>
                      ) : wizService.id === Services.HubSpot ? (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => { setHsAuthMode('oauth'); setWizError(''); }} className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${hsAuthMode === 'oauth' ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : ''}`} style={hsAuthMode !== 'oauth' ? { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' } : {}}><Trans>Your HubSpot app (OAuth)</Trans></button>
                            <button type="button" onClick={() => { setHsAuthMode('token'); setWizError(''); }} className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${hsAuthMode === 'token' ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' : ''}`} style={hsAuthMode !== 'token' ? { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' } : {}}><Trans>Access token</Trans></button>
                          </div>

                          {hsAuthMode === 'oauth' ? (
                            <>
                              <div className="rounded-xl border p-4 space-y-2.5" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Use your own HubSpot app</Trans></p>
                                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                                  <Trans>Create a HubSpot app with OAuth authentication (a project app with private distribution is enough for your own account), add the exact callback URL below as a redirect URL, and set the required and optional scopes shown. Then paste the app's Client ID and Client secret. You install the app into your HubSpot account in a popup; tokens are exchanged server-side and stored encrypted.</Trans>
                                </p>
                                <div>
                                  <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Redirect URL to register</Trans></label>
                                  <div className="flex items-center gap-2">
                                    <code className="flex-1 px-3 py-2 rounded-lg text-xs break-all" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}>{oauthRedirectUri || '…'}</code>
                                    <button type="button" onClick={() => { if (oauthRedirectUri) { navigator.clipboard?.writeText(oauthRedirectUri); showToast(t`Callback URL copied`, 'success'); } }} className="px-3 py-2 rounded-lg text-xs font-medium flex-shrink-0" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}><Trans>Copy</Trans></button>
                                  </div>
                                </div>
                                <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}><Trans>Required scopes: <span className="font-mono">{oauthScopes.join(', ') || '…'}</span></Trans></p>
                                {oauthOptionalScopes.length > 0 && (
                                  <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}><Trans>Optional scopes (custom objects, custom properties, tickets): <span className="font-mono">{oauthOptionalScopes.join(', ')}</span></Trans></p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Client ID</Trans></label>
                                <input type="text" value={hsClientId} onChange={e => { setHsClientId(e.target.value); setWizError(''); }} placeholder="00000000-0000-0000-0000-000000000000" className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Client secret</Trans></label>
                                <div className="relative">
                              <input
                                type={showHsSecret ? 'text' : 'password'}
                                value={hsClientSecret}
                                onChange={e => { setHsClientSecret(e.target.value); setWizError(''); }}
                                placeholder={t`HubSpot app Client secret`}
                                className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-mono"
                                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                              />
                              <button type="button" onClick={() => setShowHsSecret(!showHsSecret)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {showHsSecret
                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                                  }
                                </svg>
                              </button>
                            </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Use an access token</Trans></p>
                                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                                  <Trans>Create a service key (Development, then Keys) or a legacy private app in your HubSpot account with the read scopes crm.objects.contacts.read, crm.objects.companies.read and crm.objects.deals.read. Paste its access token here. The token is verified with one read before the connection is saved.</Trans>
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Access token</Trans></label>
                                <div className="relative">
                              <input
                                type={showHsToken ? 'text' : 'password'}
                                value={hsToken}
                                onChange={e => { setHsToken(e.target.value); setWizError(''); }}
                                placeholder={"pat-na1-..."}
                                className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-mono"
                                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                              />
                              <button type="button" onClick={() => setShowHsToken(!showHsToken)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {showHsToken
                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                                  }
                                </svg>
                              </button>
                            </div>
                              </div>
                            </>
                          )}
                          {wizError && <p className="text-xs text-red-500">{wizError}</p>}
                        </>
                      ) : wizService.auth === 'api_key' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>API Key</Trans></label>
                            <div className="relative">
                              <input
                                type={showKey ? 'text' : 'password'}
                                value={apiKey}
                                onChange={e => { setApiKey(e.target.value); setWizError(''); }}
                                placeholder={wizService.hint || t`Enter your API key`}
                                className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-mono"
                                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                              />
                              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {showKey
                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                                  }
                                </svg>
                              </button>
                            </div>
                          </div>
                          {wizService.hint && <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{wizService.hint}</p>}
                          {wizError && <p className="text-xs text-red-500">{wizError}</p>}
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Host</Trans></label>
                            <input type="text" value={dbHost} onChange={e => setDbHost(e.target.value)} placeholder={t`e.g. db.example.com`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Port</Trans></label>
                              <input type="text" value={dbPort} onChange={e => setDbPort(e.target.value)} placeholder={t`e.g. 5432`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Database</Trans></label>
                              <input type="text" value={dbName} onChange={e => setDbName(e.target.value)} placeholder={t`e.g. mydb`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Username</Trans></label>
                            <input type="text" value={dbUser} onChange={e => setDbUser(e.target.value)} placeholder={t`Database username`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Password</Trans></label>
                            <input type="password" value={dbPass} onChange={e => setDbPass(e.target.value)} placeholder={t`Database password`} className="w-full px-4 py-3 rounded-xl text-sm font-mono" style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }} />
                          </div>
                        </>
                      )}

                      <div className="rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-500/5 p-3 flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                        <p className="text-xs text-green-700 dark:text-green-300"><Trans>Your credentials are encrypted using AES-256 before storage and are never exposed in logs or API responses.</Trans></p>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-5">
                      <button onClick={() => setWizStep(1)} className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}><Trans>Back</Trans></button>
                      {wizService.id === Services.Salesforce ? (
                        <button onClick={startOAuth} disabled={wizSubmitting || !sfLoginUrl.trim() || !sfClientId.trim() || !sfClientSecret} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm disabled:opacity-50">
                          {wizSubmitting ? <Trans>Waiting for Salesforce…</Trans> : <Trans>Connect with Salesforce</Trans>}
                        </button>
                      ) : wizService.id === Services.HubSpot && hsAuthMode === 'oauth' ? (
                        <button onClick={startOAuth} disabled={wizSubmitting || !hsClientId.trim() || !hsClientSecret.trim()} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm disabled:opacity-50">
                          {wizSubmitting ? <Trans>Waiting for HubSpot…</Trans> : <Trans>Connect with HubSpot</Trans>}
                        </button>
                      ) : wizService.id === Services.HubSpot ? (
                        <button onClick={() => setWizStep(3)} disabled={!hsToken.trim()} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"><Trans>Continue</Trans></button>
                      ) : (
                        <button onClick={() => setWizStep(3)} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm"><Trans>Continue</Trans></button>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Configuration */}
                {wizStep === 3 && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1 space-y-4">
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}><Trans>Customize how PrivacyPal scans and processes data from this connection.</Trans></p>

                      {wizCategory === 'document_repositories' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}><Trans>Select File Types</Trans></label>
                            <div className="flex flex-wrap gap-1.5">
                              {FILE_TYPES.map(ft => {
                                const active = selectedFileTypes.includes(ft);
                                return (
                                  <button
                                    key={ft}
                                    type="button"
                                    onClick={() => setSelectedFileTypes(prev => active ? prev.filter(t => t !== ft) : [...prev, ft])}
                                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                      active ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                             : 'text-slate-400'
                                    }`}
                                    style={!active ? { borderColor: 'var(--color-border)' } : {}}
                                  >
                                    {ft}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <Toggle on={passthroughAuth} onToggle={() => setPassthroughAuth(!passthroughAuth)} label={t`Passthrough Authorization`} desc={t`Use the connecting user's own permissions when accessing files`} />
                        </>
                      )}

                      {wizCategory === 'collaboration_tools' && (
                        <>
                          <Toggle on={scanPrivate} onToggle={() => setScanPrivate(!scanPrivate)} label={t`Scan Private Channels`} desc={t`Include private channels and direct messages in privacy scans`} />
                          <Toggle on={filterBots} onToggle={() => setFilterBots(!filterBots)} label={t`Filter out known Bots`} desc={t`Automatically exclude messages from recognized bot accounts`} />
                        </>
                      )}

                      {wizCategory === 'crm' && wizService.id === Services.HubSpot && (
                        <div>
                          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Excluded HubSpot properties</Trans></label>
                          <textarea
                            value={excludedCrmFields}
                            onChange={e => setExcludedCrmFields(e.target.value)}
                            rows={3}
                            placeholder={t`Properties to skip, e.g. deals.amount, companies.secret_codename\nSeparate with commas or new lines`}
                            className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-none"
                            style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                          />
                          <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}><Trans>Use the <span className="font-mono">objectType.property</span> format with internal property names. Leave blank to scan all relevant properties.</Trans></p>
                        </div>
                      )}

                      {wizCategory === 'crm' && wizService.id !== Services.HubSpot && (
                        <div>
                          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Excluded Salesforce fields</Trans></label>
                          <textarea
                            value={excludedCrmFields}
                            onChange={e => setExcludedCrmFields(e.target.value)}
                            rows={3}
                            placeholder={t`Fields to skip, e.g. Opportunity.Amount, Account.Secret_Field__c\nSeparate with commas or new lines`}
                            className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-none"
                            style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                          />
                          <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}><Trans>Use the <span className="font-mono">Object.FieldApiName</span> format. Leave blank to scan all relevant fields.</Trans></p>
                        </div>
                      )}

                      {wizCategory === 'databases' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}><Trans>Excluded Table Names</Trans></label>
                            <textarea
                              value={excludedTables}
                              onChange={e => setExcludedTables(e.target.value)}
                              rows={3}
                              placeholder={t`Enter table names to exclude, separated by commas\ne.g. _migrations, sessions, cache`}
                              className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-none"
                              style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
                            />
                          </div>
                          <Toggle on={fullTextSearch} onToggle={() => setFullTextSearch(!fullTextSearch)} label={t`Full Text Search`} desc={t`Enable deep content scanning (adds some additional latency)`} />
                        </>
                      )}
                    </div>
                    <div className="flex gap-3 mt-5">
                      <button onClick={() => setWizStep(2)} className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}><Trans>Back</Trans></button>
                      <button onClick={submitConnection} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm"><Trans>Save & Connect</Trans></button>
                    </div>
                  </div>
                )}

                {/* Step 4: Complete */}
                {wizStep === 4 && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    {wizSubmitting && (
                      <div className="flex flex-col items-center">
                        <div className="spinner mb-4" />
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>Establishing connection...</Trans></p>
                      </div>
                    )}
                    {wizSuccess && (
                      <div className="flex flex-col items-center w-full">
                        <div className="w-14 h-14 rounded-2xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text)' }}><Trans>Connection Verified</Trans></h3>
                        <p className="text-sm mb-5" style={{ color: 'var(--color-text-muted)' }}><Trans>Your integration is active and ready. PrivacyPal will begin scanning data according to your configuration.</Trans></p>
                        <div className="w-full rounded-xl border p-4 text-left text-sm space-y-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                          <div className="flex justify-between"><span style={{ color: 'var(--color-text-muted)' }}><Trans>Service</Trans></span><span className="font-medium" style={{ color: 'var(--color-text)' }}>{wizService.name}</span></div>
                          <div className="flex justify-between"><span style={{ color: 'var(--color-text-muted)' }}><Trans>Auth Method</Trans></span><span className="font-medium" style={{ color: 'var(--color-text)' }}>{wizConnection?.authMethod === AuthMethods.OAuth2 ? 'OAuth' : wizService.auth === 'connection_string' ? t`Direct Connection` : t`API Key`}</span></div>
                          <div className="flex justify-between"><span style={{ color: 'var(--color-text-muted)' }}><Trans>Status</Trans></span><span className="font-semibold text-green-500">{wizConnection?.status || 'active'}</span></div>
                        </div>
                        <button onClick={closeWizard} className="mt-5 w-full py-3 rounded-xl font-medium text-sm transition-colors" style={{ backgroundColor: 'var(--color-text)', color: 'var(--color-surface)' }}><Trans>Done</Trans></button>
                      </div>
                    )}
                    {wizError && (
                      <div className="flex flex-col items-center w-full">
                        <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                        </div>
                        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text)' }}><Trans>Connection Failed</Trans></h3>
                        <p className="text-sm text-red-500 mb-5">{wizError}</p>
                        <button onClick={() => setWizStep(2)} className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm"><Trans>Try Again</Trans></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
